import { type } from 'arktype';
import { isDateRangeOrdered, rruleSchema } from '@zag/common/validity';

export const personLocationTypeSchema = type("'primary' | 'secondary' | 'home_office'").configure({
  description:
    "Type of person-location assignment: 'primary' is the main work location, 'secondary' is a regular alternative site, and 'home_office' indicates remote/home working.",
});

export const personLocationQuerySchema = type({
  'personId?': type('string').configure({
    description: 'Filter assignments by person ID. Optional.',
    examples: ['per_abc123'],
  }),
  'locationId?': type('string').configure({
    description: 'Filter assignments by location ID. Optional.',
    examples: ['loc_hq01'],
  }),
  'type?': personLocationTypeSchema,
  'startDate?': type('string').configure({
    description: 'Filter assignments whose validity starts on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string').configure({
    description: 'Filter assignments whose validity ends on this ISO date (YYYY-MM-DD). Optional.',
    examples: ['2026-12-31'],
  }),
}).configure({ description: 'Query parameters for filtering person-location assignments.' });

export type PersonLocationQuery = typeof personLocationQuerySchema.t;

export const createPersonLocationSchema = type({
  personId: type('string').configure({
    description: 'ID of the person being assigned to the location.',
    examples: ['per_abc123'],
  }),
  locationId: type('string').configure({
    description: 'ID of the location the person is being assigned to.',
    examples: ['loc_hq01'],
  }),
  type: personLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment starts. Optional and nullable (open-ended start).',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'ISO date (YYYY-MM-DD) when the assignment ends. Optional and nullable (open-ended end).',
    examples: ['2026-12-31'],
  }),
  'rrule?': rruleSchema.configure({
    description:
      'Optional bare iCal RRULE body (no `RRULE:` prefix, must contain `FREQ`) selecting the dates inside the window. Evaluated from `startDate`, so it never matches without one.',
    examples: ['FREQ=WEEKLY;BYDAY=FR'],
  }),
})
  .narrow(
    (value, ctx) => isDateRangeOrdered(value.startDate, value.endDate) || ctx.mustBe('a date range with endDate on or after startDate')
  )
  .configure({ description: 'Payload to assign a person to a location with an optional validity period.' });

export type CreatePersonLocation = typeof createPersonLocationSchema.t;

export const updatePersonLocationSchema = type({
  'personId?': type('string').configure({
    description: 'Updated person ID. Optional — rarely changed after creation.',
    examples: ['per_abc123'],
  }),
  'locationId?': type('string').configure({
    description: 'Updated location ID. Optional.',
    examples: ['loc_hq01'],
  }),
  'type?': personLocationTypeSchema,
  'startDate?': type('string | null | undefined').configure({
    description: 'Updated start date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-04-22'],
  }),
  'endDate?': type('string | null | undefined').configure({
    description: 'Updated end date (ISO YYYY-MM-DD). Optional and nullable.',
    examples: ['2026-12-31'],
  }),
  'rrule?': rruleSchema.configure({
    description:
      'Optional bare iCal RRULE body (no `RRULE:` prefix, must contain `FREQ`) selecting the dates inside the window. Evaluated from `startDate`, so it never matches without one. `null` removes the rule.',
    examples: ['FREQ=WEEKLY;BYDAY=FR'],
  }),
})
  .narrow(
    (value, ctx) => isDateRangeOrdered(value.startDate, value.endDate) || ctx.mustBe('a date range with endDate on or after startDate')
  )
  .configure({ description: 'Partial update payload for an existing person-location assignment.' });

export type UpdatePersonLocation = typeof updatePersonLocationSchema.t;

export const personLocationAssignmentSchema = type({
  id: 'string',
  personId: 'string',
  locationId: 'string',
  type: personLocationTypeSchema,
  startDate: 'string | null',
  endDate: 'string | null',
  'rrule?': 'string | null',
  createdAt: 'string',
  updatedAt: 'string',
}).configure({ description: 'Direct location assignment row of a person.' });

export const personLocationListItemSchema = personLocationAssignmentSchema
  .merge({
    source: "'direct' | 'inherited'",
    'locationName?': 'string',
    'locationType?': "'physical' | 'virtual'",
    'inheritedFrom?': type({ teamId: 'string', teamName: 'string' }).configure({
      description: 'Team the location is inherited through. Present only on inherited rows.',
    }),
    'effectiveStartDate?': 'string | null',
    'effectiveEndDate?': 'string | null',
  })
  .configure({ description: 'Location assignment. `source: "inherited"` rows come from team locations and are read-only.' });

export type PersonLocationAssignmentResponse = typeof personLocationListItemSchema.t;
